import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import { purgeData } from '../services/importApi';
import DateInput from '../components/DateInput';

// Only dispatch and production accumulate history; stock/order are replaced
// on every upload, so only these two are purgeable by date.
const PURGE_TYPES = [
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'production', label: 'Production' },
];

export default function DataManagementPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role && (user.role.code === 'admin' || user.role.code === 'super_admin');

  const [type, setType] = useState('dispatch');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  if (!isAdmin) {
    return (
      <div>
        <h1>Data Management</h1>
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            This section is available to administrators only. Please ask your company admin if you
            need old data removed.
          </p>
        </Card>
      </div>
    );
  }

  const handleDelete = async () => {
    setError(null);
    setResult(null);
    if (!fromDate || !toDate) {
      setError('Please choose both a From date and a To date.');
      return;
    }
    if (fromDate > toDate) {
      setError('The From date must be on or before the To date.');
      return;
    }
    const typeLabel = PURGE_TYPES.find((t) => t.value === type).label;
    const ok = window.confirm(
      `Delete ALL ${typeLabel} data from ${fromDate} to ${toDate} (inclusive)?\n\n` +
        'This permanently removes those records and cannot be undone. ' +
        'Data outside this date range is not affected.',
    );
    if (!ok) return;

    setBusy(true);
    try {
      const res = await purgeData(type, fromDate, toDate);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Could not delete the data. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>Data Management</h1>
      <p style={{ color: 'var(--color-text-secondary)', marginTop: 0 }}>
        Remove old Dispatch or Production records by date range. Use this to keep the system tidy
        once data grows over many months — for example, deleting a year that's no longer needed.
      </p>

      <Card title="Delete old data by date range">
        <div
          style={{
            background: 'var(--color-accent-warm-soft)',
            border: '1px solid var(--color-accent-warm)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-3)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <strong>Careful:</strong> deleting is permanent and cannot be undone. Only Dispatch and
          Production can be cleared here — Stock and Order are always replaced by their latest
          upload, so they don't need clearing.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
            Data type
            <select value={type} onChange={(e) => setType(e.target.value)} style={{ ...inputStyle, width: 200 }}>
              {PURGE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
            From date
            <DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ ...inputStyle, width: 180 }} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
            To date
            <DateInput value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ ...inputStyle, width: 180 }} />
          </label>

          <Button variant="danger" onClick={handleDelete} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete data in range'}
          </Button>
        </div>

        {error && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-3)' }}>
            {error}
          </p>
        )}

        {result && (
          <div
            style={{
              marginTop: 'var(--space-4)',
              background: 'var(--color-accent-soft)',
              border: '1px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Done — deleted <strong>{result.deleted}</strong>{' '}
            {PURGE_TYPES.find((t) => t.value === result.type).label} row
            {result.deleted === 1 ? '' : 's'} from {result.fromDate} to {result.toDate}.
          </div>
        )}
      </Card>
    </div>
  );
}
